/**
 * Navigation Menu Interactions
 * 
 * This script manages the interactive behavior of the navigation menu in the Chime website,
 * particularly focusing on mobile and desktop navigation experiences, menu toggling,
 * and parent-child menu item interactions.
 */

document.addEventListener('DOMContentLoaded', function() {
  // Main navigation elements
  const navParent = document.querySelector('.header__nav');
  const primaryMenu = document.getElementById('primary-menu');
  const secondaryMenu = document.getElementById('secondary-menu');

  const headerCTAContainer = document.querySelector('.header__cta-container');
  const menuParents = document.querySelectorAll('.menu-item-parent');
  const menuNonParents = document.querySelectorAll('.menu-item-non-parent');
  const subMenus = document.querySelectorAll('.header__nav-sub-menu');
  const childMenuItems = document.querySelectorAll('.menu-item-child');
  const header = document.getElementById('header'); // Get the header element

  
  // Navigation buttons for different contexts
  const navBtn = document.querySelectorAll('.nav-btn--open');
  const navBtnDeskSecondary = document.querySelector('.nav-btn--desk.nav-btn--right');
  const navBtnDeskPrimary = document.querySelector('.nav-btn--desk.nav-btn--left');
  const navBtnMobilePrimary = document.querySelector('.nav-btn--mobile.nav-btn--right');
  const navBtnMobileSecondary = document.querySelector('.nav-btn--mobile.nav-btn--left');

  // Other UI elements and class names
  const chimeLogo = document.querySelector('.header__branding');
  const openClassName = 'header__nav--opened';
  const closedClassName = 'header__nav--closed';
  const navOverlay = document.querySelector('.header__nav-overlay');
  const sliderContent = document.querySelector('.header__nav-top--content');
  const sliderDivider = document.querySelector('.header__nav-top--divider');
  
  // Create transition overlay
  const transitionOverlay = document.createElement('div');
  transitionOverlay.className = 'header__view-transition-overlay';
  navParent.appendChild(transitionOverlay);

  // Flag to ensure images are loaded only once
  let sidebarImagesLoaded = false; 

  /**
   * Loads images within the sidebar navigation that use data-src.
   */
  function loadSidebarImages() {
    if (sidebarImagesLoaded || !navParent) return; 

    const imagesToLoad = navParent.querySelectorAll('img[data-src]');
    
    imagesToLoad.forEach(img => {
      if (img.dataset.src) {
        img.src = img.dataset.src;
        img.removeAttribute('data-src');
      }
    });

    sidebarImagesLoaded = true;
  }

  /**
   * Helper function to add class(es) to DOM element
   * 
   * @param {Element} element - The DOM element
   * @param {...string} classNames - The class(es) to add
   */
  function addClass(element, ...classNames) {
    if (element && element.classList) {
      element.classList.add(...classNames);
    } else if (element) {
      element.className += ' ' + classNames.join(' ');
    }
  }

  /**
   * Helper function to remove class(es) from DOM element
   * 
   * @param {Element} element - The DOM element
   * @param {...string} classNames - The class(es) to remove
   */
  function removeClass(element, ...classNames) {
    if (element && element.classList) {
      element.classList.remove(...classNames);
    } else if (element) {
      element.className = element.className.replace(
        new RegExp('(^|\\b)' + classNames.join('|') + '(\\b|$)', 'gi'), ' '
      );
    }
  }

  /**
   * Resets the Slick slider layout.
   * Optionally jumps to a specific slide.
   * 
   * @param {number} [slideIndex] - The index of the slide to jump to.
   */
  let lastViewedSlideIndex = 0;
  function resetNavSlider(slideIndex = 0) {
    const sliderElement = document.querySelector('.header__nav-top--content .content-slider');
    if (
      typeof jQuery !== 'undefined' &&
      typeof jQuery.fn.slick !== 'undefined' &&
      sliderElement &&
      jQuery(sliderElement).hasClass('slick-initialized')
    ) {
      requestAnimationFrame(() => {
        jQuery(sliderElement).slick('setPosition');
        if (slideIndex) {
          jQuery(sliderElement).slick('slickGoTo', slideIndex, true);
        } else {
          jQuery(sliderElement).slick('slickGoTo', lastViewedSlideIndex || 0, true);
        }
      });
    }
  }

  /**
   * Helper function to check if element has a class
   * 
   * @param {Element} element - The DOM element
   * @param {string} className - The class to check for
   * @returns {boolean} - Whether the element has the class
   */
  function hasClass(element, className) {
    if (element && element.classList) {
      return element.classList.contains(className);
    } else if (element) {
      return new RegExp('(^| )' + className + '( |$)', 'gi').test(element.className);
    }
    return false;
  }

  /**
   * Handles the transition between view 1 and view 2
   * Uses CSS transitions and event listeners for smooth transitions
   * 
   * @param {Function} callback - Function to execute during the transition
   */
  function handleViewTransition(callback) {
    // Start transition - show overlay
    addClass(transitionOverlay, 'active');
    
    // Add transitioning class to menus
    addClass(primaryMenu, 'view-transitioning');
    if (secondaryMenu) {
      addClass(secondaryMenu, 'view-transitioning');
    }

    // Listen for the transition end on the overlay
    const onTransitionEnd = () => {
      // Execute view change logic
      callback();
    
      // Use setTimeout only when secondaryMenu has the view-transitioning class
      const removeTransitionClasses = () => {
        // Remove the active class to start fade out
        removeClass(transitionOverlay, 'active');
        // Remove transitioning classes
        removeClass(primaryMenu, 'view-transitioning');
      };
      
      hasClass(secondaryMenu, 'view-transitioning') 
        ? setTimeout(removeTransitionClasses, 150)
        : removeTransitionClasses();

      if (secondaryMenu) {
        removeClass(secondaryMenu, 'view-transitioning');
      }

      // Refresh the Slick slider layout after transition
      const sliderElement = document.querySelector('.header__nav-top--content .content-slider');
      if (typeof jQuery !== 'undefined' && 
          typeof jQuery.fn.slick !== 'undefined' && 
          sliderElement && 
          jQuery(sliderElement).hasClass('slick-initialized')) {
        // Small delay to ensure DOM is ready
        requestAnimationFrame(() => {
          jQuery(sliderElement).slick('setPosition');
        });
      }

      // Remove the event listener
      transitionOverlay.removeEventListener('transitionend', onTransitionEnd);
    };

    // Add the transition end listener
    transitionOverlay.addEventListener('transitionend', onTransitionEnd);
  }

  /**
   * Hides all parent menu items except the one that was clicked.
   * Creates a cleaner, focused navigation experience.
   * 
   * @param {Element} clickedParent - The parent menu item that was clicked
   */
  function hideOtherParents(clickedParent) {
    menuNonParents.forEach(item => {
      addClass(item, 'hidden');
    });

    // Hide all parent menu item roots
    menuParents.forEach(parent => {
      const root = parent.querySelector('.menu-item-parent__root');
      if (root) {
        addClass(root, 'hidden');
      }
    });

    if (sliderContent) {
      addClass(sliderContent, 'hidden');
    }
    if (sliderDivider) {
      addClass(sliderDivider, 'hidden');
    }

    // Show only the clicked parent's elements
    const links = clickedParent.querySelectorAll('a');
    links.forEach(link => {
      removeClass(link, 'hidden');
    });
    
    const subMenu = clickedParent.querySelector('.header__nav-sub-menu');
    if (subMenu) {
      removeClass(subMenu, 'hidden');
    }
  }

  /**
   * Resets the menu to its default state by showing all parent menu items.
   * Used when closing the menu or navigating back to the main menu.
   */
  function showAllMenuItems() {
    // Show all non-parent menu items
    menuNonParents.forEach(item => {
      removeClass(item, 'hidden');
    });
    
    // Reset all parent menu items to default state
    menuParents.forEach(parent => {
      const root = parent.querySelector('.menu-item-parent__root');
      if (root) {
        removeClass(root, 'active');
        removeClass(root, 'hidden');
      }
      
      const links = parent.querySelectorAll('a');
      links.forEach(link => {
        addClass(link, 'hidden');
      });
      
      const subMenu = parent.querySelector('.header__nav-sub-menu');
      if (subMenu) {
        addClass(subMenu, 'hidden');
      }
    });


    if (sliderContent) {
      removeClass(sliderContent, 'hidden');
    }
    if (sliderDivider) {
      removeClass(sliderDivider, 'hidden');
    }

    // Reset all other menu elements
    subMenus.forEach(subMenu => {
      addClass(subMenu, 'hidden');
    });
    
    removeClass(primaryMenu, 'view-two');
    
    childMenuItems.forEach(item => {
      removeClass(item, 'visible');
    });
    
    addClass(navBtnDeskSecondary, 'hidden');
    addClass(navBtnDeskSecondary, 'invisible');
    
    addClass(navBtnMobileSecondary, 'hidden');
    addClass(navBtnMobileSecondary, 'invisible');
    removeClass(navBtnMobileSecondary, 'nav-btn--back');
    
    // Update UI state
    // handleChimeLogoVisibility(false);
    // handleMobileViewTwoClasses(false);
  }

  /**
   * Animates the display of child menu items when a parent is clicked.
   * Creates a smooth transition effect for menu expansion.
   * 
   * @param {Element} parent - The parent menu item whose children should be animated
   */
  function animateChildMenuItems(parent) {
    const children = parent.querySelectorAll('.menu-item-child');
    // Reset animation state
    children.forEach(child => {
      removeClass(child, 'visible');
    });
    
    // Stagger the animation of each child item
    children.forEach((child, index) => {
      setTimeout(function() {
        addClass(child, 'visible');
      }, index * 100); // 100ms delay between each child
    });
  }

  /**
   * Opens the navigation menu.
   * Updates classes and states for proper display.
   * **Also triggers sidebar image loading.**
   */
  function openMenu() {
    // Update navigation container classes
    addClass(navParent, openClassName);
    removeClass(navParent, closedClassName);
    
    // Update button states
    addClass(navBtnDeskPrimary, 'is-active');
    removeClass(navBtnDeskPrimary, 'nav-btn--back');
    
    addClass(navBtnMobilePrimary, 'is-active');
    removeClass(navBtnMobilePrimary, 'nav-btn--back');
    
    // Add sidebar-open class to header for additional styling
    if (header) {
      addClass(header, 'sidebar-is-open');
    }

    document.body.style.overflow = 'hidden';

    loadSidebarImages(); 
    
    // Initialize the slider if it hasn't been already
    if (typeof window.initializeNavSlider === 'function') {
      window.initializeNavSlider();
    }

    // Reset slider layout when menu opens.
    resetNavSlider(0);
  }

  /**
   * Closes the navigation menu.
   * Resets all elements to their default state.
   */
  function closeMenu() {
    // Update navigation container classes
    removeClass(navParent, openClassName);
    addClass(navParent, closedClassName);

    // Reset all button states
    navBtn.forEach(btn => {
      removeClass(btn, 'is-active');
      removeClass(btn, 'nav-btn--back');
    });
    
    removeClass(navBtnDeskPrimary, 'is-active');
    removeClass(navBtnDeskPrimary, 'nav-btn--back');
    
    removeClass(navBtnMobilePrimary, 'is-active');
    removeClass(navBtnMobilePrimary, 'nav-btn--back');
    
    // Remove sidebar-open class from header
    if (header) {
      removeClass(header, 'sidebar-is-open');
    }

    document.body.style.overflow = '';

    // Reset the Slick slider layout and retain last viewed slide index
    const sliderElement = document.querySelector('.header__nav-top--content .content-slider');
    if (
      typeof jQuery !== 'undefined' &&
      typeof jQuery.fn.slick !== 'undefined' &&
      sliderElement &&
      jQuery(sliderElement).hasClass('slick-initialized')
    ) {
      lastViewedSlideIndex = jQuery(sliderElement).slick('slickCurrentSlide');
    }
  }

  /**
   * Manages the close button behavior when in "view-two" mode.
   * Ensures users can close the menu even when in a submenu.
   */
  function handleCloseButtonInViewTwo() {
    const hiddenClasses = ['invisible', 'hidden'];
    const sidebarCollapseTime = 300; // Matches sidebar closing animation duration

    removeClass(navBtnDeskPrimary, 'nav-btn--back');
    addClass(navBtnDeskPrimary, 'is-active');
    
    removeClass(navBtnDeskSecondary, 'nav-btn--expanded');
    addClass(navBtnDeskSecondary, ...hiddenClasses); 
    
    removeClass(navBtnMobileSecondary, 'nav-btn--expanded', 'nav-btn--back');
    addClass(navBtnMobileSecondary, ...hiddenClasses); 
    
    // Remove mobile view two state class
    if (header) {
      removeClass(header, 'mobile-view-two-active');
    }
    
    closeMenu();
    
    // Add a delay to wait for the sidebar closing animation to complete
    // before resetting menu items and view state
    setTimeout(() => {
      showAllMenuItems();
      removeClass(primaryMenu, 'view-two');
    }, sidebarCollapseTime);
  }

  /**
   * Handles the back button functionality in the navigation.
   * Returns to the main menu view from a submenu.
   * 
   * @param {Element} btn - The back button that was clicked
   */
  function handleBackButton(btn) {
    // First remove expanded class to collapse to dot
    removeClass(btn, 'nav-btn--expanded');
    removeClass(navBtnDeskPrimary, 'nav-btn--expanded');
    removeClass(navBtnMobileSecondary, 'nav-btn--expanded');
    
    // Immediately handle right nav button transition
    removeClass(navBtnDeskSecondary, 'nav-btn--expanded');
    addClass(navBtnDeskSecondary, 'invisible', 'hidden');

    // Then after a brief delay, complete the transition
    setTimeout(() => {
      // Reset menu to default state using transition
      handleViewTransition(() => {
        showAllMenuItems(); 
        removeClass(primaryMenu, 'view-two');
        
        // Update button state
        addClass(btn, 'is-active');
        removeClass(btn, 'nav-btn--back');
        
        // Remove mobile view two state class
        if (header) {
          removeClass(header, 'mobile-view-two-active');
        }
      });
      
      // Reopen the menu in its default state
      openMenu();
    }, 150);
  }

  /**
   * Toggles the menu open or closed based on the current state.
   * Manages the overall menu state and button appearance.
   * 
   * @param {Element} btn - The button that was clicked
   * @param {boolean} isOpen - Whether the menu is currently open
   */
  function handleMenuToggle(btn, isOpen) {
    if (isOpen && !hasClass(primaryMenu, 'view-two')) {
      // Close the menu if it's open and not in view-two
      closeMenu();
      showAllMenuItems();
      removeClass(primaryMenu, 'view-two');
      removeClass(btn, 'nav-btn--back');
    } else if (!isOpen) {
      // Open the menu if it's closed
      openMenu();
    }
  }

  /**
   * Detects if the current viewport is in mobile mode.
   * 
   * @returns {boolean} True if viewport width is below the mobile breakpoint
   */
  function isMobileView() {
    return window.innerWidth < 860; // Mobile breakpoint at 860px
  }

  /**
   * Handles clicks on the primary desktop navigation button.
   * Manages menu state based on current context.
   * 
   * @param {Event} e - The click event
   */
  function primaryDeskNavBtnClick(e) {
    e.preventDefault();
    const btn = this;

    // Handle close button in view-two
    if (hasClass(btn, 'nav-btn--close')) {
      handleCloseButtonInViewTwo();
      return;
    }

    // Handle back button
    if (hasClass(btn, 'nav-btn--back')) {
      handleBackButton(btn);
      return;
    }

    // Toggle menu open/closed
    const isOpen = hasClass(navParent, openClassName);
    handleMenuToggle(btn, isOpen);
  }

  /**
   * Handles clicks on the secondary desktop navigation button.
   * Similar to primary button but for secondary navigation elements.
   * 
   * @param {Event} e - The click event
   */
  function secondaryDeskNavBtnClick(e) {
    e.preventDefault();
    const btn = this;

    // Handle close button in view-two
    if (hasClass(btn, 'nav-btn--close')) {
      handleCloseButtonInViewTwo();
      return;
    }

    // Handle back button
    if (hasClass(btn, 'nav-btn--back')) {
      handleBackButton(btn);
      return;
    }

    // Toggle menu open/closed
    const isOpen = hasClass(navParent, openClassName);
    handleMenuToggle(btn, isOpen);
  }

  /**
   * Handles clicks on the primary mobile navigation button.
   * Specialized handling for mobile navigation.
   * 
   * @param {Event} e - The click event
   */
  function primaryMobileNavBtnClick(e) {
    e.preventDefault();
    const btn = this;
    const isOpen = hasClass(navParent, openClassName);
    const isViewTwo = hasClass(primaryMenu, 'view-two');

    // Special case: closing from view-two on mobile
    if (isOpen && isViewTwo) {
      handleViewTransition(() => {
        showAllMenuItems();
        removeClass(primaryMenu, 'view-two');
        
        // Update button states for proper transition
        removeClass(navBtnDeskPrimary, 'nav-btn--back');
        addClass(navBtnDeskPrimary, 'is-active');
        
        // Remove mobile view two state class
        if (header) {
          removeClass(header, 'mobile-view-two-active');
        }
        
        closeMenu();
      });
      return;
    }

    // Handle close button in view-two
    if (hasClass(btn, 'nav-btn--close')) {
      handleCloseButtonInViewTwo();
      return;
    }

    // Handle back button
    if (hasClass(btn, 'nav-btn--back')) {
      handleBackButton(btn);
      return;
    }

    // Toggle menu open/closed
    handleMenuToggle(btn, isOpen);
  }

  /**
   * Handles clicks on the secondary mobile navigation button.
   * Manages mobile-specific navigation for secondary buttons.
   * 
   * @param {Event} e - The click event
   */
  function secondaryMobileNavBtnClick(e) {
    e.preventDefault();
    const btn = this;
    
    // Handle back button - special case for mobile
    if (hasClass(btn, 'nav-btn--back')) {
      handleViewTransition(() => {
        showAllMenuItems();
        removeClass(primaryMenu, 'view-two');
        addClass(navBtnMobilePrimary, 'is-active');
        removeClass(navBtnMobilePrimary, 'nav-btn--back');

        // Remove mobile view two state class
        if (header) {
          removeClass(header, 'mobile-view-two-active');
        }
      });
      return;
    }

    // Handle close button in view-two
    if (hasClass(btn, 'nav-btn--close')) {
      handleCloseButtonInViewTwo();
      return;
    }

    // Toggle menu open/closed
    const isOpen = hasClass(navParent, openClassName);
    handleMenuToggle(btn, isOpen);
  }

  // Attach event handlers to navigation buttons
  if (navBtnDeskPrimary) {
    navBtnDeskPrimary.addEventListener('click', primaryDeskNavBtnClick);
  }
  
  if (navBtnDeskSecondary) {
    navBtnDeskSecondary.addEventListener('click', secondaryDeskNavBtnClick);
  }
  
  if (navBtnMobilePrimary) {
    navBtnMobilePrimary.addEventListener('click', primaryMobileNavBtnClick);
  }
  
  if (navBtnMobileSecondary) {
    navBtnMobileSecondary.addEventListener('click', secondaryMobileNavBtnClick);
  }

  // Set up parent menu item click handlers
  menuParents.forEach(parent => {
    const root = parent.querySelector('.menu-item-parent__root');
    const link = parent.querySelector('a');
    const subMenu = parent.querySelector('.header__nav-sub-menu');

    if (root) {
      // Handle clicks on parent menu items
      root.addEventListener('click', function(e) {
        e.preventDefault();

        if (hasClass(root, 'active')) {
          // If already active, reset to view one with transition
          handleViewTransition(() => {
            // First remove expanded class to collapse to dot
            removeClass(navBtnDeskPrimary, 'nav-btn--expanded');
            removeClass(navBtnMobileSecondary, 'nav-btn--expanded');
            
            // Then after a brief delay, complete the transition
            setTimeout(() => {
              showAllMenuItems();
              removeClass(primaryMenu, 'view-two');
              
              navBtn.forEach(btn => {
                addClass(btn, 'is-active');
                removeClass(btn, 'nav-btn--back');
              });
              
              // Remove mobile view two state class
              if (header) {
                removeClass(header, 'mobile-view-two-active');
              }
            }, 150);
          });
        } else {
          // If not active, activate and show submenu with transition
          handleViewTransition(() => {
            hideOtherParents(parent);
            addClass(root, 'active');
            
            if (link) {
              removeClass(link, 'hidden');
            }
            
            if (subMenu) {
              removeClass(subMenu, 'hidden');
            }
            
            addClass(primaryMenu, 'view-two');
            
            // Handle desktop button animation
            if (!isMobileView()) {
              // First set to back button state (dot)
              removeClass(navBtnDeskPrimary, 'is-active');
              removeClass(navBtnDeskPrimary, 'nav-btn--expanded');
              addClass(navBtnDeskPrimary, 'nav-btn--back');
              // Add slight delay before expanding to arrow
              setTimeout(() => {
                addClass(navBtnDeskPrimary, 'nav-btn--expanded');
              }, 150);
            }
            
            addClass(navBtnMobilePrimary, 'is-active');
            removeClass(navBtnMobilePrimary, 'nav-btn--back');
            
            removeClass(navBtnDeskSecondary, 'hidden');
            removeClass(navBtnDeskSecondary, 'invisible');
            
            // Handle mobile button state - directly to arrow state
            if (isMobileView()) {
              removeClass(navBtnMobileSecondary, 'hidden');
              removeClass(navBtnMobileSecondary, 'invisible');
              addClass(navBtnMobileSecondary, 'nav-btn--back');
              addClass(navBtnMobileSecondary, 'nav-btn--expanded');
            } else {
              // Desktop animation
              removeClass(navBtnMobileSecondary, 'hidden');
              removeClass(navBtnMobileSecondary, 'invisible');
              removeClass(navBtnMobileSecondary, 'nav-btn--expanded');
              addClass(navBtnMobileSecondary, 'nav-btn--back');
              // Add slight delay before expanding to arrow
              setTimeout(() => {
                addClass(navBtnMobileSecondary, 'nav-btn--expanded');
              }, 150);
            }
            
            // Then after a delay, expand desktop buttons to arrow if not mobile
            if (!isMobileView()) {
              setTimeout(() => {
                addClass(navBtnDeskPrimary, 'nav-btn--expanded');
                addClass(navBtnMobileSecondary, 'nav-btn--expanded');
              }, 150);
            }
            
            // Update UI state - Add mobile view two state class if mobile
            if (isMobileView() && header) {
              addClass(header, 'mobile-view-two-active');
            }
            
            // Animate child items
            animateChildMenuItems(parent);
          });
        }
      });
    }
  });

  /**
   * Handle window resize events to ensure proper UI state.
   * Adjusts button states and visibility based on viewport size.
   */
  window.addEventListener('resize', function() {
    // Fix button state if needed when resizing
    if (!hasClass(primaryMenu, 'view-two') && hasClass(navBtnDeskPrimary, 'nav-btn--back')) {
      removeClass(navBtnDeskPrimary, 'nav-btn--back');
      addClass(navBtnDeskPrimary, 'is-active');
    }
    
    // Update button state when switching to desktop in view-two
    if (hasClass(primaryMenu, 'view-two') && window.innerWidth >= 860) {
      removeClass(navBtnDeskPrimary, 'is-active');
      addClass(navBtnDeskPrimary, 'nav-btn--back');
    }
    
    // Add/Remove mobile view two class on resize if needed
    if (header) {
      const isViewTwo = hasClass(primaryMenu, 'view-two');
      if (isMobileView() && isViewTwo) {
        addClass(header, 'mobile-view-two-active');
      } else {
        removeClass(header, 'mobile-view-two-active');
      }
    }
  });

  /**
   * Handle clicks outside the navigation area.
   * Automatically closes the menu when clicking elsewhere on the page.
   */
  document.addEventListener('click', function(e) {
    if (
      !e.target.closest('.header__nav') &&
      !e.target.closest('.nav-btn--open')
    ) {
      showAllMenuItems();
      closeMenu();
    }
  });
  
  // Add event handler for the overlay specifically
  if (navOverlay) {
    navOverlay.addEventListener('click', function(e) {
      e.preventDefault();
      showAllMenuItems();
      closeMenu();
    });
  }
});
